import React, { useState, useEffect, useRef } from 'react';
import { apiService } from '../../services/api';
import { PublishedVersionSummary } from '../../types';

interface PublishVersionDialogProps {
  projectId: number;
  isOpen: boolean;
  onClose: () => void;
  onPublished: (version: PublishedVersionSummary) => void;
}

const PublishVersionDialog: React.FC<PublishVersionDialogProps> = ({
  projectId,
  isOpen,
  onClose,
  onPublished,
}) => {
  const [versionName, setVersionName] = useState('');
  const [description, setDescription] = useState('');
  const [isPublishing, setIsPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const mountedRef = useRef(true);
  const dialogTitleId = 'publish-version-dialog-title';

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (isOpen) {
      setVersionName('');
      setDescription('');
      setError(null);
      setIsPublishing(false);
      setTimeout(() => {
        nameInputRef.current?.focus();
      }, 0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handlePublish = async () => {
    const trimmedName = versionName.trim();
    if (!trimmedName) return;

    setIsPublishing(true);
    setError(null);

    try {
      const version = await apiService.publishVersion(
        projectId,
        trimmedName,
        description.trim() || undefined
      );
      if (!mountedRef.current) return;
      onPublished(version);
      onClose();
    } catch (err: any) {
      if (!mountedRef.current) return;
      const errorMessage =
        err?.response?.data?.detail || err?.message || 'Failed to publish version';
      setError(errorMessage);
      setIsPublishing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        role="dialog"
        aria-labelledby={dialogTitleId}
        aria-modal="true"
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          id={dialogTitleId}
          className="text-lg font-semibold text-gray-900 dark:text-white mb-4"
        >
          Publish Version
        </h2>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label
              htmlFor="publish-version-name"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Version Name
            </label>
            <input
              ref={nameInputRef}
              id="publish-version-name"
              type="text"
              value={versionName}
              onChange={(e) => setVersionName(e.target.value)}
              placeholder="e.g., v2.0 - Added error handling"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label
              htmlFor="publish-version-description"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Description
            </label>
            <textarea
              id="publish-version-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what changed in this version..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handlePublish}
            disabled={!versionName.trim() || isPublishing}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isPublishing ? 'Publishing...' : 'Publish'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PublishVersionDialog;
